import { IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY } from 'app/store/authSlice';
// eslint-disable-next-line you-dont-need-lodash-underscore/is-null

// define a Json type to avoid `any`
type Json = string | number | boolean | null | Json[] | { [key: string]: Json };

export const getBase64FromFile = (file: File): Promise<string> => {
  return new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => {
      res(`data:${file.type};base64,${btoa(reader.result as string)}`);
    };
    reader.onerror = rej;
    reader.readAsBinaryString(file);
  });
};

export const getCompanySelection = (): string => {
  return (localStorage.getItem('selectedPaidol') ?? '') as string;
};

export const setCompanySelection = (paidolId: string): void => {
  localStorage.setItem('selectedPaidol', paidolId);
};

export const clearIsFirstAccess = (): void => {
  localStorage.removeItem(IS_INIITAL_ACCESS_LOCAL_STORAGE_KEY);
};

export const clearCompanySelection = (): void => {
  localStorage.removeItem('selectedPaidol');
};

export const getPageName = (pathName: string): string => {
  // Remove leading slash if present
  const cleanPath = pathName.startsWith('/') ? pathName.slice(1) : pathName;
  const segments = cleanPath.split('/');
  // Only take the first two segments for the page name
  const firstTwo = segments.slice(0, 2);
  return firstTwo.join('-');
};

export function stringToColor(string: string) {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

export function removeTypename(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(removeTypename);
  } else if (obj !== null && typeof obj === 'object') {
    const { __typename, ...rest } = obj;
    return Object.keys(rest).reduce((acc, key) => {
      acc[key] = removeTypename(rest[key]);
      return acc;
    }, {} as any);
  }
  return obj;
}

export function hasAnyFields(obj: any) {
  return obj && Object.keys(obj).length > 0;
}

/**
 * Converts a JSON object into an XML string.
 * Example:
 *   const xml = jsonToXml({
 *     readByQuery: {
 *       object: 'VENDOR',
 *       fields: '*',
 *       query: "NAME like '%Speedchain%'"
 *     }
 *   });
 *   // xml === "<readByQuery><object>VENDOR</object><fields>*</fields><query>NAME like '%Speedchain%'</query></readByQuery>"
 */
export function jsonToXml(json: Json): string {
  function escapeXml(value: Json): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  }

  function build(obj: Json): string {
    let xml = '';
    if (obj === null || obj === undefined) {
      return xml;
    } else if (typeof obj !== 'object') {
      return escapeXml(obj);
    } else if (Array.isArray(obj)) {
      for (const item of obj) {
        xml += build(item);
      }
      return xml;
    } else {
      for (const key of Object.keys(obj)) {
        const val = obj[key];
        if (val === null || val === undefined) continue;
        if (Array.isArray(val)) {
          for (const item of val) {
            xml += `<${key}>${build(item)}</${key}>`;
          }
        } else if (typeof val === 'object') {
          xml += `<${key}>${build(val)}</${key}>`;
        } else {
          xml += `<${key}>${escapeXml(val)}</${key}>`;
        }
      }
      return xml;
    }
  }

  return build(json);
}

/**
 * Unicode-aware string truncation utility
 * Safely truncates a string to a maximum length without splitting Unicode characters
 * @param str - The string to truncate
 * @param maxLength - The maximum length of the resulting string
 * @returns The truncated string
 */
export const truncateUnicode = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;

  // Convert string to array of Unicode characters using spread operator
  const chars = [...str];

  // Slice to the max length and join back into a string
  return chars.slice(0, maxLength).join('');
};
